import { Component, input, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CustomCtaBaseDirective,
  ECustomFieldType,
  ICustomValueConfig,
} from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsStatusBadgeComponent } from '@shared/components/analytics-status-badge/analytics-status-badge.component';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { DeliverableAdvancedButtonComponent } from '../../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../../advanced-settings-section/deliverable-advanced-settings-section.component';
import {
  ITextDescriptionDeliverableConfig,
  TTextDescriptionDeliverableConfigOverrides,
} from './text-description-deliverable.interface';

@Component({
  imports: [
    AnalyticsStatusBadgeComponent,
    CustomRemoveButtonComponent,
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    EditableTitleComponent,
    IconComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatErrorComponent,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-text-description-deliverable',
  templateUrl: './text-description-deliverable.component.html',
})
export abstract class TextDescriptionDeliverableComponent extends CustomCtaBaseDirective implements OnInit {
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly analyticsBadgeLoading = input<boolean>(false);
  public readonly analyticsKeywordJump = input<((keyword: string) => void) | null>(null);
  public readonly analyticsKeywordJumpTargets = input<Record<string, string[]>>({});
  public readonly analyticsKeywordJumpTargetsReady = input<boolean>(true);
  public readonly config: ITextDescriptionDeliverableConfig;

  protected abstract getConfigOverrides(): TTextDescriptionDeliverableConfigOverrides;

  public canJumpToAnalyticsKeyword(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    return (this.analyticsKeywordJumpTargets()[keyword] ?? []).length > 0;
  }

  public disableAnalyticsKeywordJumpAction(keyword: string | null | undefined): boolean {
    if (!this.showAnalyticsKeywordJumpAction(keyword)) {
      return false;
    }

    return !this.canJumpToAnalyticsKeyword(keyword);
  }

  public hasAnyAnalyticsKeywordJumpTarget(): boolean {
    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    const analyticsKeywordJumpTargets: Record<string, string[]> = this.analyticsKeywordJumpTargets();

    return Object.values(analyticsKeywordJumpTargets).some((keywordTargetIds) => {
      return keywordTargetIds.length > 0;
    });
  }

  public onAnalyticsKeywordJump(keyword: string | null | undefined): void {
    if (!keyword) {
      return;
    }

    if (!this.canJumpToAnalyticsKeyword(keyword)) {
      return;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return;
    }

    analyticsKeywordJump(keyword);
  }

  public showAnalyticsKeywordJumpAction(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    if (!this.analyticsKeywordJumpTargetsReady()) {
      return false;
    }

    return this.hasAnyAnalyticsKeywordJumpTarget();
  }

  public showAnalyticsKeywordJumpLoader(keyword: string | null | undefined): boolean {
    if (!keyword) {
      return false;
    }

    const analyticsKeywordJump = this.analyticsKeywordJump();

    if (!analyticsKeywordJump) {
      return false;
    }

    if (this.analyticsKeywordJumpTargetsReady()) {
      return false;
    }

    return true;
  }

  protected get customValueConfig(): ICustomValueConfig {
    return {
      componentFields: {
        getForm: (): FormGroup => this.form(),
        managedFields: [
          { name: this.config.textFieldName, type: ECustomFieldType.string },
          { name: 'id', type: ECustomFieldType.string },
          { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
        ],
      },
    };
  }

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);
    const defaultConfig: Omit<ITextDescriptionDeliverableConfig, 'type'> = {
      highlightNamespace: 'textDescriptionDeliverable',
      placeholderTranslationKey: '',
      subtitleTranslationKey: '',
      textFieldName: 'text',
    };

    this.config = { ...defaultConfig, ...this.getConfigOverrides() };
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.config.type);
  }
}
